import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" }
const _hoisted_2 = { class: "flex items-center gap-2 text-sm mb-6" }
const _hoisted_3 = { class: "text-[#22282F] font-medium" }
const _hoisted_4 = { class: "grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" }
const _hoisted_5 = { class: "bg-white border-2 border-[#22282F] rounded-xl p-4" }
const _hoisted_6 = { class: "flex items-center gap-3" }
const _hoisted_7 = { class: "p-2 bg-[#22282F] text-white rounded-lg" }
const _hoisted_8 = { class: "font-semibold text-[#22282F]" }
const _hoisted_9 = { class: "bg-gradient-to-r from-[#22282F] to-[#22282F]/80 text-white rounded-xl p-4" }
const _hoisted_10 = { class: "flex items-center gap-3" }
const _hoisted_11 = { class: "p-2 bg-white/10 rounded-lg" }
const _hoisted_12 = { class: "font-semibold" }
const _hoisted_13 = { class: "flex items-center justify-between mb-6" }
const _hoisted_14 = { class: "text-sm text-gray-500 mt-1" }
const _hoisted_15 = { class: "mb-6 bg-white border border-gray-200 rounded-xl p-4 shadow-sm" }
const _hoisted_16 = { class: "relative" }
const _hoisted_17 = {
  key: 0,
  class: "flex items-center justify-center py-12 bg-white border border-gray-200 rounded-xl"
}
const _hoisted_18 = {
  key: 1,
  class: "bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
}
const _hoisted_19 = { class: "w-full" }
const _hoisted_20 = { class: "py-4 px-6" }
const _hoisted_21 = { class: "flex items-center gap-3" }
const _hoisted_22 = { class: "p-2 bg-gray-100 rounded-lg" }
const _hoisted_23 = { class: "font-medium text-[#22282F]" }
const _hoisted_24 = { class: "py-4 px-6" }
const _hoisted_25 = { class: "text-sm text-gray-600" }
const _hoisted_26 = { class: "py-4 px-6 text-center" }
const _hoisted_27 = { class: "inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm text-gray-700" }
const _hoisted_28 = { class: "py-4 px-6" }
const _hoisted_29 = { class: "flex items-center justify-center gap-2" }
const _hoisted_30 = ["onClick"]
const _hoisted_31 = ["onClick"]
const _hoisted_32 = {
  key: 2,
  class: "bg-white border border-gray-200 rounded-xl p-12 text-center"
}
const _hoisted_33 = { class: "text-sm text-gray-500" }
const _hoisted_34 = { class: "bg-white rounded-xl max-w-md w-full p-6" }
const _hoisted_35 = { class: "text-lg font-bold text-[#22282F] mb-4" }
const _hoisted_36 = { class: "space-y-4" }
const _hoisted_37 = { class: "flex gap-3 mt-6" }

import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Building2, Layers, Users, Plus, Edit3, Trash2, Search, ChevronRight } from 'lucide-vue-next'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

interface Direction {
  id: number
  code: string
  nom: string
}

interface Departement {
  id: number
  code: string
  nom: string
  direction: Direction
}

interface Service {
  id: number
  code: string
  nom: string
  users_count?: number
  departement: Departement
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ServicesListView',
  setup(__props) {

const toast = useToast()

const route = useRoute()
const directionId = ref(Number(route.params.directionId))
const departementId = ref(Number(route.params.departementId))

const loading = ref(false)
const direction = ref<Direction | null>(null)
const departement = ref<Departement | null>(null)
const services = ref<Service[]>([])
const searchQuery = ref('')
const showModal = ref(false)
const editingService = ref<Service | null>(null)
const form = ref({ code: '', nom: '' })

const filteredServices = computed(() => {
  if (!searchQuery.value) return services.value
  const query = searchQuery.value.toLowerCase()
  return services.value.filter(s =>
    s.nom.toLowerCase().includes(query) || s.code.toLowerCase().includes(query)
  )
})

const fetchDirection = async () => {
  try {
    direction.value = await apiFetch(`/api/admin/directions/${directionId.value}/`)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger la direction')
  }
}

const fetchDepartement = async () => {
  try {
    departement.value = await apiFetch(`/api/admin/departements/${departementId.value}/`)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger le département')
  }
}

const fetchServices = async () => {
  loading.value = true
  try {
    const allServices = await apiFetch('/api/admin/services/')
    services.value = allServices.filter((s: Service) => s.departement?.id === departementId.value)
  } catch (error: any) {
    toast.error('Erreur', 'Impossible de charger les services')
  } finally {
    loading.value = false
  }
}

const openCreateModal = () => {
  editingService.value = null
  form.value = { code: '', nom: '' }
  showModal.value = true
}

const openEditModal = (service: Service) => {
  editingService.value = service
  form.value = { code: service.code, nom: service.nom }
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingService.value = null
}

const saveService = async () => {
  try {
    const payload = {
      ...form.value,
      departement_id: departementId.value
    }

    const url = editingService.value
      ? `/api/admin/services/${editingService.value.id}/update/`
      : '/api/admin/services/create/'

    await apiFetch(url, {
      method: editingService.value ? 'PUT' : 'POST',
      body: JSON.stringify(payload)
    })

    toast.success(
      editingService.value ? 'Service modifié' : 'Service créé',
      'L\'opération a réussi'
    )
    await fetchServices()
    closeModal()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de sauvegarder le service')
  }
}

const deleteService = async (id: number) => {
  try {
    await apiFetch(`/api/admin/services/${id}/delete/`, {
      method: 'DELETE'
    })

    toast.success('Service supprimé', 'Le service a été supprimé avec succès')
    await fetchServices()
  } catch (error: any) {
    toast.error('Erreur', error.message || 'Impossible de supprimer le service')
  }
}

onMounted(() => {
  fetchDirection()
  fetchDepartement()
  fetchServices()
})

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(AdminLayout, null, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("nav", _hoisted_2, [
          _createVNode(_component_router_link, {
            to: "/organisation",
            class: "text-gray-500 hover:text-[#22282F] transition-colors"
          }, {
            default: _withCtx(() => _cache[3] || (_cache[3] = [
              _createTextVNode(" Organisation ")
            ])),
            _: 1,
            __: [3]
          }),
          _createVNode(_unref(ChevronRight), { class: "w-4 h-4 text-gray-400" }),
          _createVNode(_component_router_link, {
            to: `/organisation/directions/${directionId.value}/departements`,
            class: "text-gray-500 hover:text-[#22282F] transition-colors"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(direction.value?.nom), 1)
            ]),
            _: 1
          }, 8, ["to"]),
          _createVNode(_unref(ChevronRight), { class: "w-4 h-4 text-gray-400" }),
          _createElementVNode("span", _hoisted_3, _toDisplayString(departement.value?.nom), 1)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_unref(Building2), { class: "w-5 h-5" })
              ]),
              _createElementVNode("div", null, [
                _cache[4] || (_cache[4] = _createElementVNode("p", { class: "text-xs text-gray-500" }, "Direction", -1)),
                _createElementVNode("p", _hoisted_8, _toDisplayString(direction.value?.nom), 1)
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_9, [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("div", _hoisted_11, [
                _createVNode(_unref(Layers), { class: "w-5 h-5" })
              ]),
              _createElementVNode("div", null, [
                _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-xs text-white/70" }, "Département", -1)),
                _createElementVNode("p", _hoisted_12, _toDisplayString(departement.value?.nom), 1)
              ])
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_13, [
          _createElementVNode("div", null, [
            _cache[6] || (_cache[6] = _createElementVNode("h2", { class: "text-2xl font-bold text-[#22282F]" }, "Services", -1)),
            _createElementVNode("p", _hoisted_14, _toDisplayString(departement.value?.nom) + " • " + _toDisplayString(departement.value?.code), 1)
          ]),
          _createElementVNode("button", {
            onClick: openCreateModal,
            class: "px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors flex items-center gap-2"
          }, [
            _createVNode(_unref(Plus), { class: "w-4 h-4" }),
            _cache[7] || (_cache[7] = _createTextVNode(" Nouveau Service "))
          ])
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _createVNode(_unref(Search), { class: "absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" }),
            _withDirectives(_createElementVNode("input", {
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
              type: "text",
              placeholder: "Rechercher un service par nom ou code...",
              class: "w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20"
            }, null, 512), [
              [_vModelText, searchQuery.value]
            ])
          ])
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_17, _cache[8] || (_cache[8] = [
              _createElementVNode("div", { class: "w-8 h-8 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" }, null, -1)
            ])))
          : (filteredServices.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                _createElementVNode("table", _hoisted_19, [
                  _cache[9] || (_cache[9] = _createElementVNode("thead", { class: "bg-gray-50 border-b border-gray-200" }, [
                    _createElementVNode("tr", null, [
                      _createElementVNode("th", { class: "text-left py-4 px-6 text-sm font-semibold text-[#22282F]" }, "Service"),
                      _createElementVNode("th", { class: "text-left py-4 px-6 text-sm font-semibold text-[#22282F]" }, "Code"),
                      _createElementVNode("th", { class: "text-center py-4 px-6 text-sm font-semibold text-[#22282F]" }, "Utilisateurs"),
                      _createElementVNode("th", { class: "text-center py-4 px-6 text-sm font-semibold text-[#22282F]" }, "Actions")
                    ])
                  ], -1)),
                  _createElementVNode("tbody", null, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredServices.value, (service) => {
                      return (_openBlock(), _createElementBlock("tr", {
                        key: service.id,
                        class: "border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      }, [
                        _createElementVNode("td", _hoisted_20, [
                          _createElementVNode("div", _hoisted_21, [
                            _createElementVNode("div", _hoisted_22, [
                              _createVNode(_unref(Users), { class: "w-4 h-4 text-gray-600" })
                            ]),
                            _createElementVNode("span", _hoisted_23, _toDisplayString(service.nom), 1)
                          ])
                        ]),
                        _createElementVNode("td", _hoisted_24, [
                          _createElementVNode("span", _hoisted_25, _toDisplayString(service.code), 1)
                        ]),
                        _createElementVNode("td", _hoisted_26, [
                          _createElementVNode("span", _hoisted_27, [
                            _createVNode(_unref(Users), { class: "w-3 h-3" }),
                            _createTextVNode(" " + _toDisplayString(service.users_count || 0), 1)
                          ])
                        ]),
                        _createElementVNode("td", _hoisted_28, [
                          _createElementVNode("div", _hoisted_29, [
                            _createElementVNode("button", {
                              onClick: ($event: any) => (openEditModal(service)),
                              class: "p-2 hover:bg-gray-200 rounded-lg transition-colors",
                              title: "Modifier"
                            }, [
                              _createVNode(_unref(Edit3), { class: "w-4 h-4 text-gray-600" })
                            ], 8, _hoisted_30),
                            _createElementVNode("button", {
                              onClick: ($event: any) => (deleteService(service.id)),
                              class: "p-2 hover:bg-red-50 rounded-lg transition-colors",
                              title: "Supprimer"
                            }, [
                              _createVNode(_unref(Trash2), { class: "w-4 h-4 text-red-600" })
                            ], 8, _hoisted_31)
                          ])
                        ])
                      ]))
                    }), 128))
                  ])
                ])
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_32, [
                _createVNode(_unref(Users), { class: "w-16 h-16 text-gray-300 mx-auto mb-4" }),
                _cache[10] || (_cache[10] = _createElementVNode("h3", { class: "text-lg font-semibold text-[#22282F] mb-2" }, "Aucun service trouvé", -1)),
                _createElementVNode("p", _hoisted_33, _toDisplayString(searchQuery.value ? 'Essayez de modifier votre recherche' : 'Créez le premier service de ce département'), 1)
              ])),
        (showModal.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 3,
              class: "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50",
              onClick: _withModifiers(closeModal, ["self"])
            }, [
              _createElementVNode("div", _hoisted_34, [
                _createElementVNode("h3", _hoisted_35, _toDisplayString(editingService.value ? 'Modifier le service' : 'Nouveau service'), 1),
                _createElementVNode("div", _hoisted_36, [
                  _createElementVNode("div", null, [
                    _cache[11] || (_cache[11] = _createElementVNode("label", { class: "block text-sm font-medium text-[#22282F] mb-2" }, "Code", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.code) = $event)),
                      type: "text",
                      class: "w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20",
                      placeholder: "ex: SRV-01"
                    }, null, 512), [
                      [_vModelText, form.value.code]
                    ])
                  ]),
                  _createElementVNode("div", null, [
                    _cache[12] || (_cache[12] = _createElementVNode("label", { class: "block text-sm font-medium text-[#22282F] mb-2" }, "Nom", -1)),
                    _withDirectives(_createElementVNode("input", {
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.nom) = $event)),
                      type: "text",
                      class: "w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#22282F] focus:ring-2 focus:ring-[#22282F]/20",
                      placeholder: "ex: Service Ressources Humaines"
                    }, null, 512), [
                      [_vModelText, form.value.nom]
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_37, [
                  _createElementVNode("button", {
                    onClick: closeModal,
                    class: "flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  }, " Annuler "),
                  _createElementVNode("button", {
                    onClick: saveService,
                    class: "flex-1 px-4 py-2 bg-[#22282F] text-white rounded-lg hover:bg-[#22282F]/90 transition-colors"
                  }, _toDisplayString(editingService.value ? 'Modifier' : 'Créer'), 1)
                ])
              ])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }))
}
}

})